import styled from "@emotion/styled";
import type { SwitchProps as MuiSwitchProps } from "@mui/material";
import { Switch as MuiSwitch, Tooltip } from "@mui/material";
import { useField, useFormikContext } from "formik";
import type { ChangeEvent, ReactNode } from "react";
import { useCallback } from "react";

import { FONT_WEIGHT_NORMAL, transientOptions } from "../../utilities";
import { useFormContext } from "../FormContext";
import { useFieldContext } from "../FormFieldContext";
import { Labelish } from "../Labels";
import type { TestAttribute } from "../shared";
import { useFieldLabelAndDescription } from "./helpers";

// see also FieldCheckbox

type SwitchPlacement = "left" | "right";
type StylingProps = { checked?: boolean; disabled?: boolean };

const transition = "all 250ms";

const WIDTH = 36;
const HEIGHT = 24;
const PADDING = 4;
const THUMB_SIZE = 12;
const THUMB_MARGIN = 2;
const THUMB_TRAVEL = WIDTH - THUMB_SIZE - 2 * (PADDING + THUMB_MARGIN);

const StyledSwitch = styled(MuiSwitch)`
  &.MuiSwitch-root {
    width: ${WIDTH}px;
    height: ${HEIGHT}px;
    padding: ${PADDING}px;
  }

  .MuiSwitch-switchBase {
    padding: ${PADDING}px;

    &.Mui-checked {
      transform: translateX(${THUMB_TRAVEL}px);
      & + .MuiSwitch-track {
        opacity: 1;
      }
    }

    &.Mui-disabled + .MuiSwitch-track {
      opacity: 0.25;
      filter: grayscale(0.25);
    }
  }

  .MuiSwitch-track {
    border-radius: ${(HEIGHT - 2 * PADDING) / 2}px;
    background-color: ${({ theme }) => theme.generalTextIconVariant};
    opacity: 1;
  }

  .MuiSwitch-thumb {
    box-shadow: none;
    background-color: ${({ theme }) => theme.onPrimary};
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    margin: ${THUMB_MARGIN}px;
  }
`;

const ContainingLabel = styled.label<{ $switchPlacement?: SwitchPlacement }>`
  display: grid;
  grid-template-columns: ${({ $switchPlacement }) => ($switchPlacement === "left" ? "auto 1fr" : "1fr auto")};
  align-items: center;
  gap: 8px;

  width: 100%;
`;

const LabelContentContainer = styled.div<StylingProps>`
  display: flex;

  align-items: center;

  color: ${({ disabled, theme }) => (disabled ? theme.generalTextIconVariant : theme.generalTextIcon)};

  padding: 0 8px 0 0;

  user-select: none;

  transition: ${transition};

  > span {
    margin-right: 5px;
  }
`;

const TooltipLabel = styled(Labelish, transientOptions)<{ $hasTooltip: boolean }>`
  ${({ $hasTooltip, theme }) =>
    $hasTooltip &&
    `
    &:hover {
      color: ${theme.primaryStrong};
    }
  `}
`;

const HelperTextContainer = styled.div<StylingProps>`
  font-weight: ${FONT_WEIGHT_NORMAL};
  line-height: 1rem;
  grid-column: 1 / span 2;

  color: ${({ theme, disabled }) => (disabled ? theme.generalTextIconVariant : theme.generalTextIcon)};
  transition: ${transition};

  padding-bottom: 1rem;
`;

type SwitchProps = Omit<MuiSwitchProps, "css"> &
  TestAttribute & {
    label?: ReactNode;
    helperText?: ReactNode;
    switchPlacement?: SwitchPlacement;
    onChangeCommitted?: (value: boolean) => void;
    hideDescriptionInTooltip?: boolean;
    children?: ReactNode;
  };

export const Switch = ({
  helperText,
  label,
  color,
  switchPlacement = "right",
  hideDescriptionInTooltip,
  dataTest,
  ...fieldProps
}: SwitchProps) => {
  const { disabled, id, onChange, onChangeCommitted, ...rest } = fieldProps;

  const handleChange: SwitchProps["onChange"] = (e, value) => {
    onChange && onChange(e, value);
    onChangeCommitted?.(value);
  };

  const SwitchComponent = () => (
    <StyledSwitch
      {...rest}
      disabled={disabled}
      id={id}
      onChange={handleChange}
      color={color ?? "primary"}
      slotProps={{
        input: { "data-test": `${dataTest ?? label}-input` } as any,
      }}
    />
  );

  if (!(helperText || label)) {
    return SwitchComponent();
  }

  const showTooltip = !!hideDescriptionInTooltip && !!helperText;

  const labelDetails = (
    <LabelContentContainer disabled={disabled}>
      <Tooltip title={showTooltip ? helperText : undefined} placement="top" disableHoverListener={!showTooltip}>
        <TooltipLabel $hasTooltip={showTooltip}>{label}</TooltipLabel>
      </Tooltip>
    </LabelContentContainer>
  );

  return (
    <ContainingLabel htmlFor={id} $switchPlacement={switchPlacement}>
      {switchPlacement === "right" && labelDetails}
      {SwitchComponent()}
      {switchPlacement === "left" && labelDetails}

      {helperText && !hideDescriptionInTooltip && (
        <HelperTextContainer disabled={disabled}>{helperText}</HelperTextContainer>
      )}
    </ContainingLabel>
  );
};

type FieldSwitchProps = Omit<SwitchProps, "id" | "name" | "checked">;

export const FieldSwitch = (props: FieldSwitchProps) => {
  const { onChangeCommitted } = props;
  const { submitOnBlur } = useFormContext();
  const { submitForm } = useFormikContext();

  const { controlId, dataTest, name, disabled } = useFieldContext();
  const [{ value }, , { setValue }] = useField<boolean>(name);
  const [ambientLabel, ambientDescription] = useFieldLabelAndDescription(name);
  const label = props.label || ambientLabel;
  const description = props.helperText || ambientDescription;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      props.onChange?.(e, checked);
      void setValue(checked);
    },
    [props, setValue],
  );

  const handleChangeCommitted: SwitchProps["onChangeCommitted"] = useCallback(
    (newValue: boolean) => {
      onChangeCommitted?.(newValue);
      if (submitOnBlur) {
        void submitForm();
      }
    },
    [submitForm, submitOnBlur, onChangeCommitted],
  );

  return (
    <Switch
      {...props}
      id={controlId}
      disabled={props.disabled ?? disabled}
      name={name}
      checked={value ?? false}
      onChange={handleChange}
      label={label}
      helperText={description}
      onChangeCommitted={handleChangeCommitted}
      dataTest={dataTest}
    />
  );
};
